import { CommonModule } from '@angular/common';
import { Component, inject, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatToolbarModule } from '@angular/material/toolbar';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { AppInsightsFeatureType } from '../../../../core/models/app-insights-feature-type';
import { AppInsightsService } from '../../../../core/services/app-insights/app-insights.service';
import { AuthService } from '../../../../core/services/auth/auth.service';
import { confirmCancel, loadPageWithPatientIdParameter, setTheme } from '../../../../core/store/actions/app.actions';
import { selectTheme } from '../../../../core/store/selectors/app.selector';
import { IconComponent } from '../../../../shared/components/icon/icon.component';
import { RelationshipType } from '../../../../user/data/relationship-type';
import { User, UserPatient } from '../../../../user/models/user';
import { setPatientId } from '../../../../user/store/actions/user.actions';
import { AppInsightsEventDirective } from '../../../directives/app-insights-event-directive';
import { IconButtonComponent } from '../../icon-button/icon-button.component';
import { ProgressSpinnerComponent } from '../../progress-spinner/progress-spinner.component';
import { NavListItem } from '../models/nav-list-item';
import { NavDialogComponent } from '../nav-dialog/nav-dialog.component';

@Component({
  selector: 'app-header',
  imports: [
    CommonModule,
    IconButtonComponent,
    IconComponent,
    MatButtonModule,
    MatMenuModule,
    MatToolbarModule,
    ProgressSpinnerComponent,
    MatDividerModule,
    MatSlideToggleModule,
    FormsModule,
    LetDirective,
    AppInsightsEventDirective,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent {
  private store = inject(Store);
  public dialog = inject(MatDialog);
  private authService = inject(AuthService);
  private appInsightsService = inject(AppInsightsService);

  private selectedPatientId: number | null = null;
  private _user: User | null = null;

  @Input() navList: NavListItem[] | null = null;
  @Input() theme: string | null = null;
  @Input() hasDraft: boolean = false;
  @Input() set user(value: User | null) {
    this._user = value;
    this.initializePatient(this.selectedPatientId);
  }
  get user(): User | null {
    return this._user;
  }
  @Input() isWideScreen = false;
  @Input() set patientId(selectedPatientId: number | null) {
    this.selectedPatientId = selectedPatientId;
    this.initializePatient(selectedPatientId);
  }

  theme$ = this.store.select(selectTheme);
  patient: UserPatient | null = null;

  initializePatient(selectedPatientId: number | null): void {
    this.patient =
      this._user?.userPatients.find(p => p.patientId === selectedPatientId) ??
      this._user?.userPatients.find(p => p.isDefault) ??
      null;
  }

  onOpenMenu(): void {
    this.appInsightsService.trackEvent('App.SideNav.Opened', { feature: AppInsightsFeatureType.APP });
    this.dialog.open(NavDialogComponent, {
      id: 'mobile-menu-dialog',
      width: '100vw',
      maxWidth: '100vw',
      height: '100vh',
      maxHeight: '100vh',
      // a11y (WCAG 2.1.2 / 2.4.3): let MatDialog's CDK overlay manage focus — move focus
      // into the drawer on open (first tabbable = Close), trap it while open, close on
      // Escape, and restore focus to the "Open Menu" trigger on close. Previously this
      // overlay opted out via `disableClose`/`autoFocus: false`, breaking keyboard use.
      ariaLabel: 'Main navigation menu',
      data: {
        theme: this.theme,
        navList: this.navList,
      },
    });
  }

  goHome(): void {
    if (this._user === null) return;
    const continueAction = loadPageWithPatientIdParameter({ route: '' });
    if (this.hasDraft) {
      this.store.dispatch(confirmCancel({ continueAction }));
    } else {
      this.store.dispatch(continueAction);
    }
  }

  setTheme = (event: MatSlideToggleChange) =>
    this.store.dispatch(setTheme({ theme: event.checked ? 'dark' : 'light' }));

  logout() {
    this.authService.logout();
  }

  changePatient(patientId: number) {
    if (!this.isSelectedPatient(patientId)) {
      this.store.dispatch(setPatientId({ patientId, trigger: 'switch' }));
    } else {
      this.goHome();
    }
  }

  isSelectedPatient(patientId: number): boolean {
    return patientId === this.patient?.patientId;
  }

  notificationSettings() {
    this.store.dispatch(loadPageWithPatientIdParameter({ route: 'notification-settings' }));
  }

  giveFeedback() {
    this.store.dispatch(loadPageWithPatientIdParameter({ route: 'feedback' }));
  }

  personalInformation() {
    this.store.dispatch(loadPageWithPatientIdParameter({ route: 'personal-information' }));
  }

  /** Only a user viewing their own record can request proxy access to someone else's. */
  get canRequestProxyAccess(): boolean {
    return this.patient?.relationshipTypeId === RelationshipType.Self;
  }

  requestProxyAccess() {
    this.store.dispatch(loadPageWithPatientIdParameter({ route: 'proxy-access-form' }));
  }
}
